import type { ICurrencyInfo } from '../redux/checkout/types';
import type { ICreatePaymentIntent } from '../redux/payment/types';
import type { IWalletPaymentRail, WalletFundProvider } from '../redux/buzzbridge/types';
import { ROUTES } from '../types/global/routes.types';
import {
    defaultCheckoutProvider,
    flutterwaveCheckoutAvailable,
    paystackCheckoutAvailable,
    stripeCheckoutAvailable,
} from './paymentRail';

export const CONTEST_TOKEN_PAYSTACK_REF_KEY = 'contest_token_paystack_ref';
export const CONTEST_TOKEN_PAYSTACK_CONTEST_KEY = 'contest_token_paystack_contest_id';
export const CONTEST_TOKEN_CHECKOUT_PROVIDER_KEY = 'contest_token_checkout_provider';

export type ContestTokenHostedProvider = 'paystack' | 'flutterwave';

export function getContestTokenPaystackCallbackUrl(): string {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}${ROUTES.CONTEST_TOKEN_PAYSTACK_CALLBACK}`;
}

export function isPaystackPurchaseResponse(
    res: unknown,
): res is { provider: 'paystack'; authorization_url: string; reference: string } {
    return (
        typeof res === 'object' &&
        res != null &&
        (res as { provider?: string }).provider === 'paystack' &&
        Boolean((res as { authorization_url?: string }).authorization_url)
    );
}

/** Hosted redirect checkout (Paystack or Flutterwave) — both return authorization_url. */
export function isHostedCheckoutResponse(res: unknown): res is {
    provider: ContestTokenHostedProvider;
    authorization_url: string;
    reference?: string;
    tx_ref?: string;
} {
    if (typeof res !== 'object' || res == null) return false;
    const r = res as { provider?: string; authorization_url?: string };
    return (r.provider === 'paystack' || r.provider === 'flutterwave') && Boolean(r.authorization_url);
}

export function paystackTokenCheckoutAvailable(rail?: IWalletPaymentRail | null): boolean {
    return paystackCheckoutAvailable(rail);
}

export function flutterwaveTokenCheckoutAvailable(rail?: IWalletPaymentRail | null): boolean {
    return flutterwaveCheckoutAvailable(rail);
}

export function stripeTokenCheckoutAvailable(rail?: IWalletPaymentRail | null): boolean {
    return stripeCheckoutAvailable(rail);
}

export function defaultTokenPurchaseProvider(rail?: IWalletPaymentRail | null): WalletFundProvider {
    return defaultCheckoutProvider(rail);
}

export function storeContestTokenPaystackSession(
    reference: string,
    contestId: string,
    provider: ContestTokenHostedProvider = 'paystack',
): void {
    try {
        sessionStorage.setItem(CONTEST_TOKEN_PAYSTACK_REF_KEY, reference);
        sessionStorage.setItem(CONTEST_TOKEN_PAYSTACK_CONTEST_KEY, contestId);
        sessionStorage.setItem(CONTEST_TOKEN_CHECKOUT_PROVIDER_KEY, provider);
    } catch {
        /* ignore */
    }
}

export function readContestTokenPaystackReference(): string | null {
    try {
        return sessionStorage.getItem(CONTEST_TOKEN_PAYSTACK_REF_KEY);
    } catch {
        return null;
    }
}

export function readContestTokenPaystackContestId(): string | null {
    try {
        return sessionStorage.getItem(CONTEST_TOKEN_PAYSTACK_CONTEST_KEY);
    } catch {
        return null;
    }
}

export function readContestTokenCheckoutProvider(): ContestTokenHostedProvider | null {
    try {
        const raw = sessionStorage.getItem(CONTEST_TOKEN_CHECKOUT_PROVIDER_KEY);
        if (raw === 'paystack' || raw === 'flutterwave') return raw;
        return null;
    } catch {
        return null;
    }
}

export function clearContestTokenPaystackSession(): void {
    try {
        sessionStorage.removeItem(CONTEST_TOKEN_PAYSTACK_REF_KEY);
        sessionStorage.removeItem(CONTEST_TOKEN_PAYSTACK_CONTEST_KEY);
        sessionStorage.removeItem(CONTEST_TOKEN_CHECKOUT_PROVIDER_KEY);
    } catch {
        /* ignore */
    }
}

export interface TokenPurchaseItemDetail {
    label: string;
    value: number;
}

export function buildContestTokenHostedCheckoutBody(params: {
    amountCents: number;
    currency: string;
    contestId: string;
    packageId: string;
    userId: string;
    provider: ContestTokenHostedProvider;
    currencyInfo?: ICurrencyInfo;
    itemDetails: TokenPurchaseItemDetail[];
}): ICreatePaymentIntent {
    return {
        amount: params.amountCents,
        currency: params.currency.toLowerCase(),
        provider: params.provider,
        callback_url: getContestTokenPaystackCallbackUrl(),
        metadata: {
            category: 'tokens',
            items: [
                {
                    price: params.amountCents,
                    quantity: 1,
                    details: params.itemDetails,
                },
            ],
            metadata: {
                package_id: params.packageId,
                user_id: params.userId,
                contest_id: params.contestId,
                currencyInfo: params.currencyInfo,
            },
        },
    };
}

/** @deprecated Prefer buildContestTokenHostedCheckoutBody with explicit provider. */
export function buildContestTokenPaystackCheckoutBody(params: {
    amountCents: number;
    currency: string;
    contestId: string;
    packageId: string;
    userId: string;
    currencyInfo?: ICurrencyInfo;
    itemDetails: TokenPurchaseItemDetail[];
}): ICreatePaymentIntent {
    return buildContestTokenHostedCheckoutBody({ ...params, provider: 'paystack' });
}

export function isPaystackTokenPurchaseVerified(
    result:
        | {
              paid?: boolean;
              category?: string;
              sale_completed?: boolean;
              verified?: boolean;
          }
        | null
        | undefined,
): boolean {
    if (!result) return false;
    if (result.paid === true && result.sale_completed === true) return true;
    if (result.verified === true && result.category === 'tokens') return true;
    return false;
}

/** Detect Flutterwave return query params (transaction_id / tx_ref). */
export function isFlutterwaveCallbackParams(searchParams: URLSearchParams): boolean {
    return Boolean(searchParams.get('transaction_id') || searchParams.get('tx_ref'));
}
